import { Box, Container, Typography, Button } from '@mui/material';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../utils/redux';
import {
  setLocationInfo,
  setCurrentStep,
  setIsSubmitting,
} from '../../../utils/redux/slices/signupSlice';
import { registeredLocationSchema } from '../../../schemas';
import theme from '../../../theme';
import { useState, useEffect } from 'react';
import LocationAutocomplete from '../../../components/common/LocationAutoComplete';
import {
  getGoogleLocationsGeocode,
  signupJourneyApi,
} from '../../../utils/apis';
import { IHQLocation, ISignupParams } from '../../../schemas/apis/interface';
import { useMutation } from '@tanstack/react-query';
import EmailVerificationModal from '../../../components/modals/EmailVerificationModal';

export default function RegisteredLocation() {
  const dispatch = useDispatch();
  const { locationInfo, accountInfo, profileInfo, isSubmitting } = useSelector(
    (state: RootState) => state.signup
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] =
    useState(false);

  const { mutateAsync: signupJourney, isPending } = useMutation({
    mutationFn: (data: ISignupParams) => signupJourneyApi(data),
    onSuccess: () => {
      setIsEmailVerificationModalOpen(true);
    },
  });

  const formik = useFormik({
    initialValues: {
      registeredAddress: locationInfo.registeredAddress || '',
    },
    validationSchema: registeredLocationSchema,
    onSubmit: async values => {
      try {
        // Save form data to Redux store
        dispatch(
          setLocationInfo({
            registeredAddress: values.registeredAddress,
          })
        );

        // Set submitting state
        dispatch(setIsSubmitting(true));

        let hqLocation: IHQLocation;

        try {
          // Attempt to get geocode data
          const geocodeResponse = await getGoogleLocationsGeocode({
            query: values.registeredAddress,
          });

          const geocodeData = geocodeResponse.data.results[0];
          const addressComponents = geocodeData.address_components;

          // Extract address components
          const getComponent = (type: string, short = false) => {
            const component = addressComponents.find((comp: any) =>
              comp.types.includes(type)
            );
            return component
              ? short
                ? component.short_name
                : component.long_name
              : '';
          };

          // Create HQ location object based on geocode data
          hqLocation = {
            name: profileInfo.businessName || '',
            location_code: getComponent('postal_code') || '',
            address_line1:
              geocodeData.formatted_address || values.registeredAddress,
            address_line2: '',
            city:
              getComponent('locality') ||
              getComponent('administrative_area_level_2') ||
              '',
            state: getComponent('administrative_area_level_1') || '',
            country: getComponent('country') || '',
            zip_code: getComponent('postal_code') || '',
            phone_number: '',
            email: accountInfo.email,
            latitude: geocodeData.geometry.location.lat || 0,
            longitude: geocodeData.geometry.location.lng || 0,
            location_type: geocodeData.geometry.location_type || 'HQ',
            is_active: true,
          };
        } catch (geocodeError) {
          console.warn(
            'Geocoding failed, using fallback location data:',
            geocodeError
          );
          // Fallback location data if geocoding fails
          hqLocation = {
            name: profileInfo.businessName || '',
            location_code: '',
            address_line1: values.registeredAddress,
            address_line2: '',
            city: '',
            state: '',
            country: '',
            zip_code: '',
            phone_number: '',
            email: accountInfo.email,
            latitude: 0,
            longitude: 0,
            location_type: 'HQ',
            is_active: true,
          };
        }

        // Prepare complete signup data
        const signupData: ISignupParams = {
          name: profileInfo.fullName || '',
          email: accountInfo.email,
          password: accountInfo.password,
          businessName: profileInfo.businessName || '',
          hqLocation: hqLocation,
          accountType: accountInfo.accountType ?? 'trial',
        };

        // Call the signup API
        await signupJourney(signupData);

        // Navigate to success page or dashboard
        dispatch(setIsSubmitting(false));
      } catch (error) {
        console.error('Error during signup:', error);
        dispatch(setIsSubmitting(false));
        // You might want to show an error message to the user here
      }
    },
  });

  // Check if form is valid whenever values or errors change
  useEffect(() => {
    const { registeredAddress } = formik.values;
    const hasValues = registeredAddress.trim() !== '';
    const hasNoErrors = Object.keys(formik.errors).length === 0;

    setIsFormValid(hasValues && hasNoErrors);
  }, [formik.values, formik.errors]);

  const handleBack = () => {
    dispatch(setCurrentStep(2));
  };

  // Handle location selection from the autocomplete component
  const handleLocationChange = (location: string) => {
    formik.setFieldValue('registeredAddress', location);
  };

  return (
    <Box
      sx={{
        flex: '1 1 50%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 2, md: 2, lg: 2 },
        bgcolor: 'white',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            px: '24px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: '#2B2D3F',
              fontSize: '24px',
            }}
          >
            Where Are You Located?
          </Typography>
          <Typography
            sx={{
              color: '#6B6F80',
              fontSize: '14px',
            }}
          >
            Add your registered business address to get started
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mb: 2, px: '24px', width: '100%' }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
              Registered Address
            </Typography>
            <LocationAutocomplete
              value={formik.values.registeredAddress}
              onChange={handleLocationChange}
              placeholder="Enter your business address"
              error={
                formik.touched.registeredAddress &&
                Boolean(formik.errors.registeredAddress)
              }
              helperText={
                formik.touched.registeredAddress &&
                formik.errors.registeredAddress
                  ? String(formik.errors.registeredAddress)
                  : undefined
              }
              width="450px"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: '24px',
              mt: 4,
              width: '100%',
            }}
          >
            <Button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              sx={{
                py: 1.5,
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '14px',
                height: '34px',
                width: '10%',
              }}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isFormValid || isPending}
              sx={{
                py: 1.5,
                bgcolor: theme.palette.primary.main,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '14px',
                height: '34px',
                width: '100%',
                '&.Mui-disabled': {
                  bgcolor: '#E0E0E0',
                  color: '#9E9E9E',
                },
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  boxShadow: 'none',
                  scale: '1.02',
                },
              }}
            >
              {isSubmitting || isPending ? 'Submitting...' : 'Complete'}
            </Button>
          </Box>
        </form>
      </Container>
      <EmailVerificationModal
        open={isEmailVerificationModalOpen}
        onClose={() => setIsEmailVerificationModalOpen(false)}
      />
    </Box>
  );
}
